import { OrderBrandsAPI } from '@business/api/order';
import { OrderBrandId, OrderBrandResponse } from '@business/entities/order';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class OrderBrandBloc extends BaseBloc {
  private readonly brandsRepo = new Repository<OrderBrandResponse>({
    getItemId: brand => brand.id,
  });

  readonly brands$ = this.brandsRepo.items$;
  readonly selectBrand = this.brandsRepo.selectItem;

  onReset() {
    this.brandsRepo.reset();
  }

  fetchBrands = () => {
    return OrderBrandsAPI.fetchBrands().pipe(this.brandsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_brands_error') }));
  };

  fetchBrand = (id: OrderBrandId) => {
    return OrderBrandsAPI.fetchBrand(id).pipe(
      this.brandsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_brands_error') }),
    );
  };

  createBrand = (data: Partial<OrderBrandResponse>) => {
    return OrderBrandsAPI.createBrand(data).pipe(
      this.brandsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_brand'),
      }),
    );
  };

  updateBrand = (id: OrderBrandId, data: Partial<OrderBrandResponse>) => {
    return OrderBrandsAPI.updateBrand(id, data).pipe(
      this.brandsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_brand'),
      }),
    );
  };

  deleteBrand = (id: OrderBrandId) => {
    return showDeleteConfirmation('Delete Brand', `Do you really want to remove this brand?`).pipe(
      switchMap(() => OrderBrandsAPI.deleteBrand(id)),
      this.brandsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_brand'),
      }),
    );
  };

  sync = () => {
    return OrderBrandsAPI.sync().pipe(
      this.brandsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_order_product'),
      }),
    );
  };
}
